import {addDynamicScriptTag} from '../utils.js';
import {User, UserProvider} from '../user.js';

const SDK_VERSION = '1.2.3';

const TestAvatar = '/model_original_1739996301.vrm';
const TestHeadIcon = '/headicon_1739996301.vrm';

export interface ViverseConfig {
    clientId: string;
    clientName: string;
    cookieDomain: string;
}
interface Result {
    state: string;
    access_token: string;
    account_id: string;
    expire_in: number;
}

interface ViverseClient {
    handleRedirectCallback(): Promise<Result>;

    loginWithRedirect(args: {
        /* Added to the callback URL */
        state?: string;
        redirectionUrl?: string;
    }): ViverseClient;
}

type Avatar = {
    id: string | number;
    isPrivate: boolean;
    vrmUrl: string;
    headIconUrl: string;
    snapshot: string;
    createTime: number;
    updateTime: number;
};

interface Profile {
    name: string;
    activeAvatar: Avatar | null;
}
interface ViverseAvatarClient {
    getProfile(): Promise<Profile>;
    getAvatarList(): Promise<Avatar[]>;
    getActiveAvatar(): Promise<Avatar | null>;
    getPublicAvatarList(): Promise<Avatar[]>;
    getPublicAvatarByID(id: string): Promise<Avatar | null>;
    getAvatarFileWithSDK(url: string): Promise<ArrayBuffer | null>;
}

declare global {
    interface Window {
        viverse: {
            client: new (args: {
                clientId: string;
                /* HTC account domain */
                domain: string;
                cookieDomain?: string;
            }) => ViverseClient;

            avatar: new (args: {
                baseURL: 'https://sdk-api.viverse.com';
                token?: string;
            }) => ViverseAvatarClient;
        };
    }
}

const APP_ID = '8t45chz62k';

export class ViverseProvider implements UserProvider {
    name = 'viverse';

    _user: User | null = null;
    onReady: Promise<User>;
    ready = false;

    avatarName: string;
    vrmUrl: string;

    viverseClient: Promise<ViverseClient>;
    avatarClient?: ViverseAvatarClient;

    constructor(config: ViverseConfig) {
        // For localhost, short‐circuit:
        if (window.location.hostname === 'localhost') {
            this._user = {
                name: 'Jonathan',
                profilePictureUrl: TestHeadIcon,
                avatarUrl: TestAvatar,
            };
            this.ready = true;
            this.onReady = Promise.resolve(this._user);
            return;
        }

        const script = document.createElement('script');
        script.crossOrigin = 'no-cors';
        script.defer = true;
        script.src =
            'https://www.viverse.com/static-assets/viverse-sdk/1.2.9/viverse-sdk.umd.js';
        script.onload = () => this.onInit();
        document.head.appendChild(script);
    }

    async onInit() {
        const APP_ID = '8t45chz62k';
        globalThis.viverseClient = new globalThis.viverse.client({
            clientId: APP_ID,
            domain: 'account.htcvive.com',
        });

        const authResult = await globalThis.viverseClient.checkAuth();
        if (!authResult) {
            console.log('[VIVERSE] Not authenticated, redirecting to login.');
            globalThis.viverseClient.loginWithWorlds({state: '{}'});
            throw new Error('User not authenticated');
        }

        console.log('[VIVERSE] Authentication result:', authResult);

        const accessToken = await globalThis.viverseClient.getToken();
        if (!accessToken) {
            throw new Error('[VIVERSE] Failed to retrieve access token.');
        }

        globalThis.avatarClient = new globalThis.viverse.avatar({
            baseURL: 'https://sdk-api.viverse.com/',
            token: accessToken,
        });

        const profile = await globalThis.avatarClient.getProfile();
        const vrmUrl = profile?.activeAvatar?.vrmUrl;
        const name = profile?.name;

        if (name && vrmUrl) {
            console.log('[VIVERSE] Active Avatar Name:', name);
            console.log('[VIVERSE] VRM URL:', vrmUrl);

            this._user = {
                name,
                profilePictureUrl: vrmUrl,
                avatarUrl: vrmUrl,
            };
            //return [name, vrmUrl];
        }
    }

    get user() {
        return this._user;
    }

    async requestLogin(): Promise<User> {
        this.onInit();

        if (!this._user) {
            throw new Error('Not logged in');
        }

        return this._user;
    }

    get isLoggedIn() {
        return !!this._user;
    }
}
